import { useState, useMemo } from 'react';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { ChevronsUpDown, Check, Building2, Home as HomeIcon } from 'lucide-react';

/**
 * LotPickerCombobox — V14.62 Sprint 17.
 *
 * Filterable dropdown alternative to the left sidebar — lets the user
 * pick any of (potentially 145+) lots without scrolling a long list.
 * Shows a compact summary in the trigger : current lot label + lot count.
 * On open, displays a search input + grouped list (by building) with
 * highlight on the active row.
 */
const LotPickerCombobox = ({ lots, selectedLotId, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const selectedLot = useMemo(
    () => (lots || []).find((l) => l.lot_id === selectedLotId),
    [lots, selectedLotId],
  );

  // Group lots by building for the dropdown ; lots without a building
  // appear under "— Sans bâtiment —".
  const groups = useMemo(() => {
    const map = new Map();
    (lots || []).forEach((l) => {
      const key = l.building_name || '— Sans bâtiment —';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    return Array.from(map.entries())
      .map(([title, items]) => ({
        title,
        items: items.slice().sort(
          (a, b) => (a.floor_level ?? 99) - (b.floor_level ?? 99)
                 || (a.label || a.name || a.lot_id).localeCompare(b.label || b.name || b.lot_id),
        ),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [lots]);

  const triggerLabel = selectedLot
    ? (selectedLot.label || selectedLot.lot_number || selectedLot.name || selectedLot.lot_id.slice(-6))
    : `Sélectionner un lot…`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="inline-flex items-center gap-1.5 h-7 px-2 text-[11px] rounded border bg-black/20 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderColor: 'var(--c-border)', color: 'var(--c-text)' }}
          data-testid="lot-picker-combobox-trigger"
          title="Sélecteur rapide de lot"
        >
          <HomeIcon className="w-3 h-3 text-amber-400" />
          <span className="truncate max-w-[180px]">{triggerLabel}</span>
          <span className="text-gray-500">·</span>
          <span className="text-gray-500">{(lots || []).length}</span>
          <ChevronsUpDown className="w-3 h-3 opacity-60 ml-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[320px]"
        style={{ background: 'var(--c-bg)', borderColor: 'var(--c-border)' }}
        data-testid="lot-picker-combobox-content"
      >
        <Command>
          <CommandInput placeholder="Rechercher un lot…" className="h-8 text-[11px]"
                        data-testid="lot-picker-combobox-search" />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>Aucun lot ne correspond.</CommandEmpty>
            {groups.map((g) => (
              <CommandGroup key={g.title}
                heading={
                  <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-wide text-gray-500">
                    <Building2 className="w-3 h-3 text-amber-400/70" />
                    {g.title} · {g.items.length}
                  </span>
                }
              >
                {g.items.map((lot) => {
                  const label = lot.label || lot.lot_number || lot.name || `Lot ${lot.lot_id.slice(-6)}`;
                  const sub = [lot.floor_name, lot.typology].filter(Boolean).join(' · ');
                  const isSel = lot.lot_id === selectedLotId;
                  return (
                    <CommandItem
                      key={lot.lot_id}
                      value={`${label} ${sub} ${lot.lot_id}`}
                      onSelect={() => { onSelect(lot.lot_id); setOpen(false); }}
                      data-testid={`lot-picker-item-${lot.lot_id}`}
                      className="cursor-pointer text-[11px]"
                    >
                      <Check className={`w-3 h-3 mr-1.5 ${isSel ? 'opacity-100 text-amber-400' : 'opacity-0'}`} />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{label}</span>
                        {sub && (
                          <span className="block text-[9.5px] text-gray-500 truncate">{sub}</span>
                        )}
                      </span>
                      {lot.checklist?.plan_reviewed && (
                        <span className="ml-1 text-[8.5px] text-emerald-300">REVU</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default LotPickerCombobox;
